// Config
import { featureGroups } from './config';


import { logger } from './utils';


import {
	clone,
	find,
	get,
	indexOf,
	intersection,
	map,
	partition,
	remove,
	startsWith,
	truncate,
	uniq,
	without
} from 'lodash';


/**
 * “Reducer” for our state. Mimicks reducers as seen in Redux stores.
 *
 * @method reducer
 * @param  {Object} state  		Current state.
 * @param  {Object} action 		Values for updating setting.
 * @return {Object} 			Updated state.
 */
export default function reducer(state, action) {
	switch (action.type) {
		case 'SET_FONTS': {
			return {
				...state,
				fonts: action.products
			};
		}


		case 'SET_PRODUCT_FEATURES': {
			const data = get(action, 'productFeatures.data');
			return {
				...state,
				productFeatures: {
					...state.productFeatures,
					[data.id]: data
				}
			};
		}


		case 'ALIGNMENT': {
			return {
				...state,
				settings: {
					...state.settings,
					alignment: {
						...state.settings.alignment,
						source: action.source,
						value: action.value
					}
				}
			};
		}

		case 'SIZE': {
			return {
				...state,
				settings: {
					...state.settings,
					size: {
						...state.settings.size,
						source: action.source,
						value: action.value
					}
				}
			};
		}

		case 'TRACKING': {
			return {
				...state,
				settings: {
					...state.settings,
					tracking: {
						...state.settings.tracking,
						source: action.source,
						value: action.value
					}
				}
			};
		}


		case 'LEADING': {
			return {
				...state,
				settings: {
					...state.settings,
					leading: {
						...state.settings.leading,
						source: action.source,
						value: action.value
					}
				}
			};
		}

		case 'PM': {
			return {
				...state,
				settings: {
					...state.settings,
					pm: {
						...state.settings.PM,
						source: action.source,
						value: action.value
					}
				}
			};
		}


		/**
		 * Family and Style setup
		 * @type {Object}
		 */
		case 'SET_FAMILY': {
			const { id, silent, source } = action;
			const { productFeatures } = state;

			const prevSelectedFamily = parseInt(get(state, 'settings.selectedFamily.id'), 10) || 0;
			const family = productFeatures[id];

			if (
				!family || !id
				|| id === prevSelectedFamily
			) { return state; }

			logger({
				enabled: false,
				label: `REDUCER: ${action.type}`,
				lines: [{
					label: 'name',
					data: family.label
				}, {
					label: 'id',
					data: parseInt(id, 10)
				}, {
					label: 'new family',
					data: id !== prevSelectedFamily
				}, {
					label: 'prevSelectedFamily',
					data: prevSelectedFamily
				}, {
					label: 'processDOM',
					data: action.processDOM || false
				}, {
					label: 'source',
					data: source
				}, {
					label: 'silent',
					data: silent
				}]
			});

			return {
				...state,
				settings: {
					...state.settings,
					prevSelectedFamily,
					selectedFamily: {
						...state.settings.selectedFamily,
						processDOM: action.processDOM || false, // Forcing false so it doesn’t give us undefined
						id: parseInt(id, 10),
						label: family.label,
						silent,
						slug: family.slug,
						source,
						styles: family.styles,
						value: family.value
					}
				}
			};
		}


		case 'SET_FAMILY_BY_NAME': {
			const { name, silent, source } = action;

			const {
				fonts,
				productFeatures
			} = state;

			// If action.name === false, set `selectedFamily` to `null`. This
			// will be used to change select menu label from, say “Requiem” to
			// the generic “Font family”.
			if (!name) {
				return {
					...state,
					settings: {
						...state.settings,
						selectedFamily: null
					}
				};
			}

			const familyData = find(fonts, selectedFont =>
				selectedFont.attributes.slug === name.toLowerCase()
			);

			if (familyData === undefined) { return state; }

			const family = productFeatures[parseInt(familyData.id, 10)];

			const prevSelectedFamily = parseInt(get(state, 'settings.selectedFamily.id'), 10) || 0;

			logger({
				enabled: false,
				label: `REDUCER: ${action.type}`,
				lines: [{
					label: 'name',
					data: family.label
				}, {
					label: 'id',
					data: parseInt(family.id, 10)
				}, {
					label: 'new family',
					data: family.id !== prevSelectedFamily
				}, {
					label: 'prevSelectedFamily',
					data: prevSelectedFamily
				}, {
					label: 'processDOM',
					data: action.processDOM || false
				}, {
					label: 'source',
					data: source
				}, {
					label: 'new state:',
					data: {
						prevSelectedFamily,
						selectedFamily: {
							...state.settings.selectedFamily,
							id: parseInt(family.id, 10),
							label: family.label,
							silent,
							slug: family.slug,
							source,
							styles: family.styles,
							value: family.value
						}
					}
				}]
			});

			return {
				...state,
				settings: {
					...state.settings,
					prevSelectedFamily,
					selectedFamily: {
						...state.settings.selectedFamily,
						id: parseInt(family.id, 10),
						label: family.label,
						silent,
						slug: family.slug,
						source,
						styles: family.styles,
						value: family.value
					}
				}
			};
		}


		case 'SET_STYLE': {
			const { selectedFamily } = state.settings;

			// If action.styleID === false, set `selectedStyle` to `null`. This
			// will be used to change select menu label from, say Isotope Black”
			// to the generic “Font style”.

			// If `styleID` is false we set selectedStyle to null. This means
			// the font styles menu in the Controls will says “Font styles”
			// This conditional MUST “action.styleID === false” and not
			// “!action.styleID” because we don’t want to catch null or
			// undefined here.
			if (action.styleID === false) {
				return {
					...state,
					settings: {
						...state.settings,
						selectedStyle: null
					}
				};
			}

			if (!selectedFamily || !selectedFamily.id) {
				return state;
			}

			const theFamilyID = action.familyID || get(state, 'settings.selectedFamily.id');

			// Get the family using the familyID
			const theFamily = get(state, `productFeatures[${theFamilyID}]`);

			// If no style ID is passed, get the default style for the
			// selected family.
			const defaultStyleID = parseInt(action.styleID, 10) || theFamily.value;

			// Get the style using the styleID
			const theStyle = find(theFamily.styles, style => style.id === defaultStyleID);

			if (!theStyle) { return state; }

			// Check style’s `features` array to ensure the default figure
			// type is represented there, but only if there are other
			// figure types.
			const theFigureFeatures = intersection(featureGroups.figures, map(theStyle.features, 'code'));
			const featuresContainDefault = indexOf(theFigureFeatures, theStyle.defaultFigure) > -1;

			// Only show Figure features if there are multiples
			if (featuresContainDefault && theFigureFeatures.length === 1) {
				remove(theStyle.features, { code: theStyle.defaultFigure });
			} else if (!featuresContainDefault && theFigureFeatures.length) {
				theStyle.features.push({ code: theStyle.defaultFigure });
			}

			const prevSelectedStyle = parseInt(get(state, 'settings.selectedStyle.id'), 10) || 0;

			return {
				...state,
				settings: {
					...state.settings,
					prevSelectedStyle,
					selectedStyle: {
						processDOM: action.processDOM || false, // Forcing false so it doesn’t give us undefined
						defaultFigure: theStyle.defaultFigure,
						defaultStyles: theStyle.defaultStyles,
						features: theStyle.features,
						id: parseInt(theStyle.id, 10),
						label: theStyle.label,
						relatedPackage: theStyle.related_item,
						silent: action.silent,
						source: action.source,
						ssm: theStyle.ssm,
						style: theStyle.defaultStyles.style,
						stylisticSets: theStyle.stylisticSets,
						value: theStyle.value,
						weight: theStyle.defaultStyles.weight
					}
				}
			};
		}

		/**
		 * OpenType Features.
		 * =====================================================================
		 */
		case 'SET_OPENTYPE_FEATURES': {
			const { selectedStyle } = state.settings;
			const { silent, value: features } = action;

			if (
				!features ||
				features === undefined ||
				typeof features !== 'string'
			) {
				const hasLigatures = find(selectedStyle.features, (feature) => feature.code === 'liga');

				return {
					...state,
					settings: {
						...state.settings,
						otFeatures: {
							...state.settings.otFeatures,
							silent: false,
							source: action.source,
							value: hasLigatures ? ['liga', selectedStyle.defaultFigure] : [selectedStyle.defaultFigure]
						},
						stylisticSets: {
							...state.settings.stylisticSets,
							silent: false,
							source: action.source,
							value: []
						}
					}
				};
			}

			// Create array from features string.
			// `action.features` needs to be passed in as a concatenated string
			// of values with a hyphen delimiter.
			// 		Example: liga-smcp-ss01-ss02-ss03-ss04-ss05
			const theFeatures = features.split('-');

			// Now partition the array into OpenType features and StylisticSets.
			// Creates an array of elements split into two groups: stylisticSets and openType.
			const featuresPartitioned = partition(theFeatures, (item) => startsWith(item, 'ss'));

			const stylisticSets = uniq(featuresPartitioned[0]);
			const openType = uniq(featuresPartitioned[1]);

			return {
				...state,
				settings: {
					...state.settings,
					otFeatures: {
						...state.settings.otFeatures,
						silent,
						source: action.source,
						value: openType
					},
					stylisticSets: {
						...state.settings.stylisticSets,
						silent,
						source: action.source,
						value: stylisticSets
					}
				}
			};
		}


		case 'TOGGLE_OPENTYPE_FEATURE': {
			const { settings } = state;

			// Clone `otFeatures` array to modify
			let newFeaturesArray = { ...settings.otFeatures.value };

			// Ensure values are truncated to 4 chars
			newFeaturesArray = map(newFeaturesArray, (feature) => truncate(feature, { length: 4, omission: '' }));

			// Is the requested setting a Figure feature?
			const isFigureSetting = indexOf(featureGroups.figures, action.value) > -1;

			// If the toggled setting is a Figure…
			if (isFigureSetting) {
				newFeaturesArray = remove(newFeaturesArray, item => indexOf(featureGroups.figures, item) < 0);
				newFeaturesArray.push(action.value);
			} else {
				const isInArray = indexOf(settings.otFeatures.value, action.value) > -1;

				// If toggled feature is in the array, remove it.
				if (isInArray) {
					newFeaturesArray = without(newFeaturesArray, action.value);
				// If not, add it.
				} else { newFeaturesArray.push(action.value); }
			}

			return {
				...state,
				settings: {
					...state.settings,
					otFeatures: {
						...state.otFeatures,
						silent: action.silent,
						source: action.source,
						value: newFeaturesArray
					}
				}
			};
		}


		case 'TOGGLE_STYLISTIC_SET': {
			const { settings } = state;

			let newSSArray = clone(settings.stylisticSets.value);
			const isInArray = indexOf(settings.stylisticSets.value, action.value) > -1;

			if (isInArray) {
				newSSArray = without(newSSArray, action.value);
			} else { newSSArray.push(action.value); }

			return {
				...state,
				settings: {
					...state.settings,
					stylisticSets: {
						...state.settings.stylisticSets,
						silent: action.silent,
						source: action.source,
						value: newSSArray
					}
				}
			};
		}


		case 'TOGGLE_EDITOR_STATE': {
			return {
				...state,
				editorIsEditable: action.isEditable
			};
		}


		case 'UPDATE_EDITOR_CONTENT': {
			return {
				...state,
				editorContent: action.content
			};
		}


		case 'SET_EDITOR_IS_DIRTY': {
			return {
				...state,
				editorIsDirty: action.value
			};
		}


		case 'INITIAL_SETUP_IS_COMPLETE': {
			return {
				...state,
				initialSetupDone: action.status
			};
		}


		case 'INTERACTION_INITIATED': {
			return {
				...state,
				interactionInitiated: action.value || false
			};
		}


		case 'SET_FOCUS': {
			return {
				...state,
				editorIsFocused: action.value
			};
		}


		case 'IS_TEXT_SELECTED': {
			return {
				...state,
				editorTextIsSelected: action.value
			};
		}


		case 'SELECTION_LENGTH': {
			return {
				...state,
				selectionLength: action.value
			};
		}


		case 'CARET_POSITION': {
			const anchor = parseInt(action.anchor, 10);
			const offset = parseInt(action.offset, 10);
			const caretPosition = anchor - (anchor + offset);

			return {
				...state,
				caretPosition
			};
		}


		case 'SET_ACTIVE_LAYOUT_TYPE': {
			return {
				...state,
				activeLayoutType: action.type
			};
		}


		case 'SET_ACTIVE_PANEL': {
			return {
				...state,
				activePanel: action.panel === state.activePanel ? null : action.panel
			};
		}


		case 'SET_ACTIVE_MENU': {
				return {
				...state,
				activeMenu: action.menu
			};
		}


		case 'IS_LOADING': {
			return {
				...state,
				isLoading: action.value
			};
		}


		case 'SET_DOM_PROCESSED': {
			if (!action.whichType) { return state; }

			const type = action.whichType;
			const types = {
				family: 'selectedFamily',
				style: 'selectedStyle'
			};

			return {
				...state,
				settings: {
					...state.settings,
					[types[type]]: {
						...state.settings[types[type]],
						processDOM: false
					}
				}
			};
		}


		case 'UPDATE_LAYOUT': {
			return {
				...state,
				updateLayout: action.value
			};
		}


		case 'SET_EDITOR_IS_READY': {
			return {
				...state,
				editorIsReady: action.value
			};
		}


		case 'SELECTION_COLLAPSED': {
			return {
				...state,
				selectionIsCollapsed: action.value
			};
		}


		default:
			return state;
	}
}
